'use strict';
import { isEmpty, isObject, isString, isBoolean, isNeArray, isNeString } from '@aretheyhappy/utils';
/**
 * Default configuration
 */
const DEFAULT_CONFIG = {
    fallback_locale: 'en',
};
/**
 * Regex used to find parameters inside a locale definition
 */
const PARAMETER_REGEX = /\{\{[^}]{0,50}\}\}/g;
/**
 * Serialize translation evaluation with provided parameters
 * @param obj
 * @param params
 * @returns
 */
function serialize(obj, params) {
    if (isEmpty(obj))
        throw new Error('[I18N] Obj should be passed as a non empty object @serialize');
    if (!isObject(params))
        throw new Error('[I18N] Params should be passed as an object @serialize');
    if (obj.params === false)
        return obj.raw;
    const obj_params = obj.params;
    return Object.keys(obj_params).reduce((acc, param) => {
        const value = params[param];
        return acc.replace(obj_params[param] || '', params.hasOwnProperty(param) && value !== undefined
            ? isString(value) ? value.trim() : value
            : '');
    }, obj.raw);
}
class I18NCore {
    /**
     * Initialize I18NCore
     * @param options
     * @returns
     */
    static initialize(options = DEFAULT_CONFIG) {
        //  Check if the passed configuration was in the correct format
        if (!isObject(options))
            throw new Error('[I18N] The passed options should be an object');
        if (options.hasOwnProperty('fallback_locale') && (!isString(options.fallback_locale) || isEmpty(options.fallback_locale))) {
            throw new Error('[I18N] Fallback locale should be passed as a non empty string');
        }
        this.config = options;
        this.initialized = true;
    }
    /**
     * Extend Locale
     * @param locale
     * @param data
     */
    static extendLocale(locale, data) {
        if (!this.initialized)
            throw new Error('[I18N] Please instantiate i18n first @extendLocale');
        if (!isString(locale) || isEmpty(locale))
            throw new Error('[I18N] Locale should be passed as a non empty string @extendLocale');
        if (!isObject(data))
            throw new Error('[I18N] Json data should be passed as a non empty object @extendLocale');
        const map = new Map();
        //  Recursively loop to fill the map
        const looper = (cursor, prefix) => {
            if (isObject(cursor)) {
                const cursorMap = cursor;
                Object.keys(cursor)
                    .forEach((key) => looper(cursorMap[key] !== undefined ? cursorMap[key] : {}, prefix ? `${prefix}.${key}` : `${key}`));
            }
            else {
                let match;
                const params = {};
                //  Parse through the string to define parameters for replacement
                while ((match = PARAMETER_REGEX.exec(cursor)) !== null) {
                    params[match[0].replace(/{/g, '').replace(/}/g, '').trim()] = new RegExp(match[0], 'g');
                }
                //  Set on the map, if params is not empty, add it to the evaluation, if it is set it to false
                map.set(prefix !== false ? prefix : '', {
                    raw: cursor,
                    params: !isEmpty(params) ? params : false,
                });
            }
        };
        looper(data, false);
        if (!this.map.hasOwnProperty(locale)) {
            //  Configure map for this locale
            this.map[locale] = map;
        }
        else {
            // Extend map
            const currentMap = this.map[locale];
            this.map[locale] = new Map([...currentMap, ...map]);
        }
    }
    /**
     * Merge an array of raw locale packs and return a new locale pack
     *  (eg: [{a: 'Hello'}, {b: 'Hi there'}, {a: 'Welcome', c: 'Cool'}] -> {a: 'Welcome', b: 'Hi there', c: 'Cool'}
     * @param packs
     * @returns
     */
    static mergeRawPacks(packs) {
        if (!isNeArray(packs))
            return {};
        const merge = (target, source, prefix = '') => {
            if (isEmpty(source) || !isObject(source))
                return target;
            const acc = target;
            for (const key of Object.keys(source)) {
                // Guard against prototype pollution by skipping dangerous keys
                if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
                    continue;
                }
                if (isObject(source[key])) {
                    if (acc[key] && isString(acc[key])) {
                        throw new Error(`{${prefix}${key}} Locale pack has pre-existing primitive, cant assign nested content`);
                    }
                    acc[key] = merge(acc[key] || {}, source[key], `${prefix}${key}.`);
                }
                else if (isString(source[key])) {
                    if (acc[key] && isObject(acc[key])) {
                        throw new Error(`{${prefix}${key}} Locale pack has pre-existing nested content, cant assign primitive`);
                    }
                    acc[key] = source[key];
                }
            }
            return acc;
        };
        //  Recursive merge function
        let target = {};
        for (const pack of packs)
            target = merge(target, pack);
        return target;
    }
    /**
     * Verify if a translation key exists on the map
     * @param locale
     * @param key
     * @returns
     */
    static has(locale, key) {
        if (!this.initialized)
            throw new Error('[I18N] Please instantiate i18n first @has');
        if (!isNeString(locale))
            throw new Error('[I18N] Locale should be passed as a non empty string @has');
        if (!isNeString(key))
            throw new Error('[I18N] Key should be passed as a non empty string @has');
        return this.map[locale] !== undefined && this.map[locale].has(key.trim());
    }
    /**
     * Translate the provided key
     * @param locale Language
     * @param key I18N language key
     * @param params Parameters to provide to the I18N language key
     * @param nullable (default=false) If set to true will return null if serialization not found
     * @returns
     */
    static go(locale, key, params = {}, nullable = false) {
        if (!this.initialized)
            throw new Error('[I18N] Please instantiate i18n first @go');
        //  If locale is not a non-empty string, throw
        if (!isNeString(locale))
            throw new Error('[I18N] Locale should be passed as a non empty string @go');
        //  If key is not a string, throw
        if (!isString(key))
            throw new Error('[I18N] Key should be passed as a string @go');
        //  If trimmed key is an empty string, return an empty string
        if (key.trim().length === 0)
            return '';
        if (!isObject(params))
            throw new Error('[I18N] Params should be passed as an object @go');
        if (!isBoolean(nullable))
            throw new Error('[I18N] Nullable should be passed as an object @go');
        //  If found in the locale map, return that one
        if (this.has(locale, key)) {
            const locale_map = this.map[locale];
            if (locale_map === undefined)
                return '';
            const locale_object = locale_map.get(key.trim());
            if (locale_object === undefined)
                return '';
            return serialize(locale_object, params);
        }
        //  If found in the fallback locale map, return that one
        if (this.has(this.config.fallback_locale, key)) {
            const locale_map = this.map[this.config.fallback_locale];
            if (locale_map === undefined)
                return '';
            const locale_object = locale_map.get(key.trim());
            if (locale_object === undefined)
                return '';
            return serialize(locale_object, params);
        }
        //  Return key string if not found at all
        return !nullable ? key.trim() : null;
    }
    /**
     * Translate the provided key
     * @param locale Language
     * @param key I18N language key
     * @param params Parameters to provide to the I18N language key
     * @returns
     */
    static run(locale, key, params = {}) {
        return this.go(locale, key, params);
    }
}
I18NCore.config = { fallback_locale: 'en' };
I18NCore.map = {};
I18NCore.initialized = false;
export { I18NCore };
export default I18NCore;
//# sourceMappingURL=I18NCore.js.map